import type { ChangeEvent, InputHTMLAttributes } from 'react'

import cx from 'classnames'
import { useCallback, useState } from 'react'
import { MdOutlineClose } from 'react-icons/md'
// @ts-expect-error // It's fine, it's not typed
import MaskedInput from 'react-maskedinput'

import styles from './input.module.css'

type InputProps = {
  errorMessage?: string
  rule?: string
  value: string
  onBlur?: () => void
  onFocus?: () => void
  onChange?: (value: string) => void
  isClearable?: boolean
  helperText?: string
  mask?: string

} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'>

export function Input ({ id, onChange, value, placeholder, errorMessage, type = 'text', helperText, onBlur, onFocus, rule, required, isClearable, ...rest }: InputProps): JSX.Element {
  const [ error, setError ] = useState(false)
  const [ accept, setAccept ] = useState(false)
  const [ focused, setFocused ] = useState(false)

  let InputComponent: React.ElementType = 'input'

  if (typeof rest.mask === 'string') {
    InputComponent = MaskedInput
  }

  const validateValue = useCallback((value: string) => {
    if (rule) {
      try {
        if (value.match(rule)) {
          setAccept(true)
          setError(false)
        } else {
          setError(true)
          setAccept(false)
        }
      } catch (e) {
        console.error(e)
      }
      return
    }

    if (required) {
      if (value.length > 0) {
        setAccept(true)
        setError(false)
      } else {
        setError(true)
        setAccept(false)
      }
    }
  }, [ rule, required ])

  const handleBlur = useCallback(() => {
    setFocused(false)
    validateValue(value)

    onBlur?.()
  }, [ onBlur, validateValue, value ])

  const handleFocus = useCallback(() => {
    setFocused(true)
    onFocus?.()
  }, [ onFocus ])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if ((error || accept) || event.target.value.length - value.length > 1) {
      validateValue(event.target.value)
    }

    onChange?.(event.target.value)
  }, [ accept, error, onChange, validateValue, value.length ])

  return (
    <div
      className={cx(styles.inputWrapper, {
        [styles.disabled as string]: rest.disabled
      })}
    >
      <label
        htmlFor={id}
        className={cx(styles.inputLabel, {
          [styles.focused as string]: focused,
          [styles.date as string]: type === 'date',
          [styles.empty as string]: !value || value.length === 0
        })}
      >
        {placeholder} {required && (<span style={{ position: 'absolute', top: -3, right: -7, fontSize: '1rem', color: 'red' }}>*</span>)}
      </label>
      <div style={{ position: 'relative' }}>
        <InputComponent
          id={id}
          type={type}
          required={required}
          value={value}
          className={cx(styles.input, [ error && styles.error ])}
          placeholder={(placeholder ?? '') + (required ? '*' : '')}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
        {isClearable && value.length > 0 && (
          <button
            onClick={() => onChange?.('')}
            className={cx(styles.clearButton, {
              [styles.focused as string]: focused
            })}
          >
            <MdOutlineClose size='1.1rem' />
          </button>
        )}
      </div>

      {errorMessage && (
        <span style={{ paddingTop: '0.25rem', paddingLeft: '0.375rem', fontSize: '0.75rem', display: 'block', color: '#ff4e47' }}>
          {errorMessage}
        </span>
      )}

      {!errorMessage && helperText && (
        <span style={{ paddingTop: '0.25rem', paddingLeft: '0.375rem', fontSize: '0.75rem', color: '#333', display: 'block' }}>
          {helperText}
        </span>
      )}
    </div>

  )
}
